import { useDosingWeight, useRenal, useRegimen } from '@/store/selectors';
import { Collapsible } from '@/components/shared/Collapsible';

/** Educational: shows the formulas used for the current computation (PSD §8.4 / TODO Task 1.29). */
export function EquationsView(): JSX.Element | null {
  const dw = useDosingWeight();
  const renal = useRenal();
  const regimen = useRegimen();

  if (!dw || !renal) return null;

  return (
    <Collapsible title="Equations & calculations" defaultOpen={false}>
      <div className="equations-content" data-testid="equations-view">
        <h4 className="eq-section">Dosing weight</h4>
        <pre className="eq-block">
{`IBW (Devine):
  Male:   50 + 2.3 × (height[in] − 60)
  Female: 45.5 + 2.3 × (height[in] − 60)

AdjBW (if ABW > 120% IBW):
  AdjBW = IBW + 0.4 × (ABW − IBW)

Selected: ${dw.selectionRule} = ${dw.selectedWeightKg.toFixed(1)} kg`}
        </pre>

        <h4 className="eq-section">Renal function</h4>
        <pre className="eq-block">
{`Cockcroft-Gault CrCl:
  CrCl = ((140 − age) × weight) / (72 × SCr)
  × 0.85 if female

  = ((140 − ?) × ${dw.selectedWeightKg.toFixed(1)}) / (72 × ${renal.crClScrUsedMgDl})
  = ${renal.crClMlMin.toFixed(1)} mL/min`}
        </pre>

        <h4 className="eq-section">Population kₑl</h4>
        <pre className="eq-block">
{`kₑl = 0.00293 × CrCl + 0.014
    = 0.00293 × ${renal.crClMlMin.toFixed(1)} + 0.014
    = ${renal.populationKelPerHr.toFixed(4)} hr⁻¹`}
        </pre>

        {regimen && (
          <>
            <h4 className="eq-section">Regimen</h4>
            <pre className="eq-block">
{`Dose = ${regimen.empiricDoseMg.toFixed(0)} mg
Interval = ${regimen.eiInterval ?? regimen.empiricIntervalHr}h
Predicted peak = ${regimen.predictedPeakMg?.toFixed(1) ?? '—'} mg/L
Predicted trough = ${regimen.predictedTroughMg?.toFixed(1) ?? '—'} mg/L`}
            </pre>
          </>
        )}
      </div>
    </Collapsible>
  );
}
